import type { Step } from '@neopolis/engine';

import { T } from '../textes-interface.js';

/**
 * Le fil d'Ariane des étapes du chapitre.
 *
 * Une étape déjà franchie reste accessible — on doit pouvoir revenir relire la
 * carte. Une étape au-delà de la progression est **verrouillée** : le bouton est
 * désactivé, et son état est annoncé aux lecteurs d'écran.
 */
export function FilAriane({
  etapes,
  etapeCourante,
  etapeMaximale,
  onAller,
}: {
  etapes: readonly Step[];
  etapeCourante: number;
  etapeMaximale: number;
  onAller: (stepIndex: number) => void;
}) {
  return (
    <nav aria-label={T.etapes.titre}>
      <ol className="flex flex-wrap gap-2">
        {etapes.map((etape, index) => {
          const verrouillee = index > etapeMaximale;
          const courante = index === etapeCourante;
          const terminee = index < etapeMaximale;

          const etat = verrouillee
            ? T.etapes.verrouillee
            : courante
              ? T.etapes.enCours
              : terminee
                ? T.etapes.terminee
                : T.etapes.aller;

          return (
            <li key={etape.etape} className="flex-1">
              <button
                type="button"
                disabled={verrouillee}
                aria-current={courante ? 'step' : undefined}
                aria-label={`${etape.etape}. ${etape.titre} — ${etat}`}
                onClick={() => onAller(index)}
                className={`flex min-h-touch w-full items-center justify-center gap-2 rounded-xl px-3 text-center font-semibold transition ${
                  courante
                    ? 'bg-sky-600 text-white'
                    : verrouillee
                      ? 'cursor-not-allowed bg-slate-100 text-slate-400'
                      : 'bg-white text-slate-700 hover:bg-sky-50'
                }`}
              >
                <span aria-hidden="true" className="text-sm opacity-70">
                  {etape.etape}
                </span>
                <span className="truncate">{etape.titre}</span>
                {verrouillee && <span aria-hidden="true">🔒</span>}
                {terminee && <span aria-hidden="true">✓</span>}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
